/**
 * @module useStallMutations
 * @purpose Stall/unstall mutations for a collection request, split out of useCollectionRequestMutations to stay under the max-lines-per-function guardrail.
 * @depends stallRequest/unstallRequest services, runMutation, invalidatingThen, ListInvalidationContext
 * @dependedOnBy useCollectionRequestMutations (use-collection-requests.ts)
 *
 * @rules
 * - State: shares the parent hook's `updating`/`error` state via passed-in setters -- no state of its own
 * - Errors: ApiError message surfaced through runMutation's fallback path
 */

import { useCallback } from 'react'
import type { CollectionRequest, StallRequestInput } from '../../types/collection-requests'
import { stallRequest, unstallRequest } from '../../services/collection-requests'
import { runMutation } from './run-mutation'
import { invalidatingThen } from './invalidating-then'
import type { ListResourceKey } from '../use-list-invalidation'

interface UseStallMutationsArgs {
  setUpdating: (busy: boolean) => void
  setError: (message: string | null) => void
  invalidate: (keys: ListResourceKey[]) => void
}

interface UseStallMutationsResult {
  stall: (
    caseId: string,
    requestId: string,
    input: StallRequestInput
  ) => Promise<CollectionRequest | null>
  unstall: (caseId: string, requestId: string) => Promise<CollectionRequest | null>
}

/**
 * Stall/unstall mutations, composed into useCollectionRequestMutations's return shape.
 *
 * @behaviour Mirrors overrideStatus's mutation shape exactly: runMutation for
 * loading/error state, invalidatingThen for the ListInvalidationContext subscription.
 * @errors ApiError message surfaced via runMutation; fallback is null on failure.
 */
export function useStallMutations({
  setUpdating,
  setError,
  invalidate,
}: UseStallMutationsArgs): UseStallMutationsResult {
  const stall = useCallback(
    (caseId: string, requestId: string, input: StallRequestInput) =>
      runMutation<CollectionRequest | null>(
        {
          setLoading: setUpdating,
          setError,
          errorMessage: 'Failed to mark request as stuck with client',
          fallback: null,
        },
        () =>
          invalidatingThen(stallRequest(caseId, requestId, input), invalidate, [
            'collection_requests',
            'case_activity',
          ])
      ),
    [setUpdating, setError, invalidate]
  )

  const unstall = useCallback(
    (caseId: string, requestId: string) =>
      runMutation<CollectionRequest | null>(
        {
          setLoading: setUpdating,
          setError,
          errorMessage: 'Failed to resume stalled request',
          fallback: null,
        },
        () =>
          invalidatingThen(unstallRequest(caseId, requestId), invalidate, [
            'collection_requests',
            'case_activity',
          ])
      ),
    [setUpdating, setError, invalidate]
  )

  return { stall, unstall }
}
